/** @jsxImportSource theme-ui */
import { Heading, Flex, Label, Checkbox, Text } from 'theme-ui';
import { CheckIcon, ReplyIcon } from '@heroicons/react/solid';
import { useState } from 'react';
import { toast } from 'react-toastify';
import {
    useGetVossErrorLogsQuery,
    useResolveVossErrorLogsMutation,
    useReplayVossErrorLogMutation,
} from '@/store/apiService';
import Button from '@/components/Button';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import DataTable from '@/components/DataTable';
import { format } from 'date-fns';
import { Container } from '@/components/styled';

const typeLabels: Record<string, string> = {
    VossError: 'Voss error',
    FailedCreatingCompanyInVoss: 'Customer creation failed',
    FailedEditingCompanyInVoss: 'Customer update failed',
    FailedCreatingCompanyLicenseInVoss: 'License creation failed',
    FailedEditingCompanyLicenseInVoss: 'License update failed',
    FailedCreatingSubscriptionTerminationInVoss: 'Termination failed',
    FailedDeletingSubscriptionTerminationInVoss: 'Termination cancel failed',
    FailedEditingCompanyLicenseInBokaMera: 'Local license update failed',
    FailedCreatingCompanyUsageInVoss: 'Usage reporting failed',
    LicenseSubscriptionMismatch: 'License ↔ Voss mismatch (audit)',
    LicenseAuditRunFailed: 'Audit run failed',
};

const typeLabel = (row: any) => typeLabels[row.TypeName] ?? row.TypeName ?? `Type ${row.TypeId}`;

const ActionsCell = ({ row }: { row: { original: any } }) => {
    const log = row.original;
    const [confirmReplay, setConfirmReplay] = useState(false);
    const [resolveLogs, resolveState] = useResolveVossErrorLogsMutation();
    const [replayLog, replayState] = useReplayVossErrorLogMutation();

    const onToggleResolved = () => {
        resolveLogs({ VossErrorLogIds: [log.Id], Resolved: !log.Resolved })
            .unwrap()
            .catch((err) => {
                toast.error(err?.data?.ResponseStatus?.Message ?? err?.message ?? 'Failed', {
                    autoClose: 5000,
                });
            });
    };

    return (
        <Flex sx={{ justifyContent: 'flex-end', flexGrow: 1, alignItems: 'center', gap: 2 }}>
            {confirmReplay ? (
                <ConfirmationModal
                    hideOnClickOutside
                    loading={replayState.isLoading}
                    headingText="Replay Voss operation"
                    content="This re-enqueues the original failed operation and marks this row resolved once it is enqueued. If the root cause is not fixed, it will fail again and create a new row. Continue?"
                    onNo={() => setConfirmReplay(false)}
                    onYes={() => {
                        replayLog({ Id: log.Id })
                            .unwrap()
                            .then(() => {
                                setConfirmReplay(false);
                                toast.success('Replay enqueued');
                            })
                            .catch((err) => {
                                setConfirmReplay(false);
                                toast.error(
                                    err?.data?.ResponseStatus?.Message ??
                                        err?.message ??
                                        'Replay failed',
                                    { autoClose: 5000 },
                                );
                            });
                    }}
                />
            ) : null}
            {log.MessageType && !log.Resolved ? (
                <Button
                    variant="secondary"
                    title="Replay the original operation"
                    isLoading={replayState.isLoading}
                    onClick={() => setConfirmReplay(true)}
                >
                    <Flex sx={{ alignItems: 'center', gap: 1 }}>
                        <ReplyIcon width={16} height={16} />
                        Replay
                    </Flex>
                </Button>
            ) : null}
            <Button
                variant={log.Resolved ? 'secondary' : 'primary'}
                title={log.Resolved ? 'Reopen this row' : 'Mark as handled'}
                isLoading={resolveState.isLoading}
                onClick={onToggleResolved}
            >
                {log.Resolved ? 'Reopen' : 'Resolve'}
            </Button>
        </Flex>
    );
};

const columns = [
    { columnKey: 'Id', accessor: 'Id', Header: 'Id', width: '60px' },
    { columnKey: 'CompanyId', accessor: 'CompanyId', Header: 'Company Id' },
    {
        columnKey: 'TypeName',
        accessor: 'TypeName',
        Header: 'Type',
        Cell: ({ row }: any) => <>{typeLabel(row.original)}</>,
    },
    { columnKey: 'SourceName', accessor: 'SourceName', Header: 'Source', width: '90px' },
    { columnKey: 'Comments', accessor: 'Comments', Header: 'Comments', width: '35%' },
    {
        columnKey: 'Resolved',
        accessor: 'Resolved',
        Header: 'Resolved',
        width: '70px',
        Cell: ({ row }: any) => (
            <Flex sx={{ width: '20px', height: '20px' }}>
                {row.original.Resolved ? <CheckIcon sx={{ stroke: 'green.4' }} /> : null}
            </Flex>
        ),
    },
    {
        columnKey: 'CreatedDate',
        accessor: 'CreatedDate',
        Header: 'Created date',
        width: '160px',
        Cell: ({ row }: any) => format(new Date(row.original.CreatedDate), 'd MMM yyyy, HH:mm:ss'),
    },
    { columnKey: 'Actions', accessor: 'Actions', Header: '', Cell: ActionsCell },
];

const VossErrorLogs = () => {
    const [showResolved, setShowResolved] = useState(false);
    const errorLogs = useGetVossErrorLogsQuery(showResolved ? {} : { Resolved: false });

    return (
        <Container
            sx={{
                table: {
                    whiteSpace: 'normal',
                },
                td: {
                    textAlign: 'center',
                },
                'td > div': {
                    margin: '0 auto',
                },
            }}
        >
            <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Heading>Voss error logs</Heading>
                <Label sx={{ width: 'auto', alignItems: 'center' }}>
                    <Checkbox
                        checked={showResolved}
                        onChange={(e) => setShowResolved(e.target.checked)}
                    />
                    <Text>Show resolved</Text>
                </Label>
            </Flex>
            <DataTable
                data={errorLogs?.data?.Results}
                columns={columns}
                isLoading={errorLogs.isLoading}
                isFetching={errorLogs.isFetching}
            />
        </Container>
    );
};

export default VossErrorLogs;
